import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home, Calendar, ClipboardList, User, LogOut, Users, Camera, History,
  MoreHorizontal, ArrowLeft, NotebookPen, ShoppingBag,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RolePreviewToggle } from './RolePreviewToggle';
import { RolePreviewBanner } from './RolePreviewBanner';
import { ImpersonationBanner } from './impersonation/ImpersonationBanner';

/** Phone-width column used app-wide — every portal screen inherits it. */
export const PORTAL_COL = 'w-full max-w-[430px] mx-auto';

// Screen titles for the header (longest prefix wins). Anything not on the
// bottom bar is a sub-screen and gets a back arrow.
const TITLES = [
  ['/portal/invoice/create', 'New invoice'],
  ['/edit', 'Edit invoice'],
  ['/portal/invoice/', 'Invoice'],
  ['/portal/invoice', 'Invoices'],
  ['/portal/my-captures/', 'Capture'],
  ['/portal/my-captures', 'My captures'],
  ['/portal/capture', 'Capture'],
  ['/portal/site-notes/new', 'Add site note'],
  ['/edit', 'Edit site note'],
  ['/portal/site-notes', 'Site notes'],
  ['/portal/orders/new', 'New order'],
  ['/portal/orders/', 'Order'],
  ['/portal/orders', 'Orders'],
  ['/portal/team-schedule', 'Team schedule'],
  ['/portal/schedule', 'Schedule'],
  ['/portal/job/', 'Job'],
  ['/portal/surveys', 'Surveys'],
  ['/portal/new-project', 'New project'],
  ['/portal/profile', 'Profile'],
  ['/portal', 'Home'],
];
const titleFor = (path) => (TITLES.find(([p]) => path === p || (p.endsWith('/') ? path.startsWith(p) : path.startsWith(p + '/') || path === p)) || [])[1] || 'Portal';

/**
 * Bottom bar.
 *  - Operatives & sub-contractors: Home · Schedule · Notes · Profile (no More menu).
 *    Capture / Orders / Invoices / Building Control stay reachable as Home cards.
 *  - Surveyors & admins keep their team bar; the first 5 go on the bar, extras fold into More.
 */
function buildNav(user, features) {
  const isAdmin = user?.user_type === 'admin' || user?.roles?.includes('admin');
  const isSurveyor = user?.user_type === 'surveyor' || user?.roles?.includes('surveyor');
  const isSub = user?.user_type === 'sub_contractor';
  const canCapture = !isSub && (isAdmin || isSurveyor || !!user?.capture_enabled);

  if (!isSurveyor && !isAdmin) {
    return [
      { path: '/portal', icon: Home, label: 'Home' },
      { path: '/portal/schedule', icon: Calendar, label: 'Schedule' },
      { path: '/portal/site-notes', icon: NotebookPen, label: 'Notes' },
      { path: '/portal/profile', icon: User, label: 'Profile' },
    ];
  }
  const items = [
    { path: '/portal', icon: Home, label: 'Home' },
    { path: '/portal/team-schedule', icon: Users, label: 'Schedule' },
  ];
  if (canCapture) items.push({ path: '/portal/capture', icon: Camera, label: 'Capture' });
  if (canCapture && features?.purchase_orders) items.push({ path: '/portal/orders', icon: ShoppingBag, label: 'Orders' });
  items.push({ path: '/portal/profile', icon: User, label: 'Profile' });
  // Secondary — folded into More when the bar is full
  items.push({ path: '/portal/site-notes', icon: NotebookPen, label: 'Site notes' });
  items.push({ path: '/portal/surveys', icon: ClipboardList, label: 'Surveys' });
  if (canCapture) items.push({ path: '/portal/my-captures', icon: History, label: 'My captures' });
  return items;
}

const isActivePath = (pathname, path) => pathname === path || (path !== '/portal' && pathname.startsWith(path + '/'))
  || (path === '/portal/invoice' && pathname.startsWith('/portal/invoice'))
  || (path === '/portal/schedule' && pathname.startsWith('/portal/job/'));

export function PortalLayout({ children }) {
  const { user, logout, features, isViewingAsUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const all = buildNav(user, features);
  const primary = all.length <= 5 ? all : all.slice(0, 4);
  const overflow = all.length <= 5 ? [] : all.slice(4);
  const pathname = location.pathname;
  const isSubScreen = pathname !== '/portal'; // back arrow everywhere but Home (owner request)
  const title = titleFor(pathname);

  const goBack = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate('/portal');
  };
  const handleLogout = () => { logout(); navigate('/login'); };
  const isAdmin = user?.user_type === 'admin' || user?.roles?.includes('admin');

  return (
    <div className="min-h-screen bg-[#141922]" data-testid="portal-shell">
      <div className={`${PORTAL_COL} min-h-screen bg-[#1E2530] flex flex-col relative sm:border-x sm:border-white/5 sm:shadow-2xl`} data-testid="portal-column">
        {/* Sticky top cluster — preview bar sits above the header on EVERY portal screen */}
        <div className="sticky top-0 z-50 pt-safe">
          <ImpersonationBanner scope="portal" />
          <RolePreviewBanner scope="portal" />
          <header className="bg-[#2A3240] border-b border-white/10 px-3 py-3 flex items-center justify-between min-h-[60px]">
          <div className="flex items-center gap-2 min-w-0">
            {isSubScreen ? (
              <button type="button" onClick={goBack} className="w-11 h-11 -ml-1 rounded-xl flex items-center justify-center text-white hover:bg-white/10 active:scale-95" aria-label="Back" data-testid="portal-back-btn">
                <ArrowLeft className="w-6 h-6" />
              </button>
            ) : (
              <button type="button" onClick={() => navigate('/portal')} className="w-10 h-10 bg-[#F5A623] rounded-xl flex items-center justify-center active:scale-95" aria-label="Home" data-testid="portal-logo-home">
                <span className="text-[#1E2530] font-bold text-sm">LUX</span>
              </button>
            )}
            <span className="text-white font-semibold text-lg truncate" data-testid="portal-title">{title}</span>
            {isAdmin && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]" data-testid="portal-admin-badge">Admin</Badge>}
          </div>
          <div className="flex items-center gap-1">
            <RolePreviewToggle theme="dark" />
            {!isViewingAsUser && <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white hover:bg-white/10 min-h-[44px] min-w-[44px] active:scale-95 p-2" data-testid="logout-button" title="Logout">
              <LogOut className="w-5 h-5" />
            </Button>}
          </div>
        </header>
        </div>

        <main className="flex-1 pb-24" data-testid="portal-main">
          {children}
        </main>

        <nav className={`fixed bottom-0 left-1/2 -translate-x-1/2 ${PORTAL_COL} bg-[#2A3240] border-t border-white/10 safe-area-pb z-40`} data-testid="portal-bottom-nav">
          <div className="flex items-center justify-around">
            {primary.map(({ path, icon: Icon, label }) => {
              const active = isActivePath(pathname, path);
              return (
                <button key={path} onClick={() => navigate(path)}
                  className={`relative flex flex-col items-center justify-center gap-1 py-2.5 px-1 rounded-xl transition-all flex-1 min-h-[60px] active:scale-95 ${active ? 'text-[#F5A623] bg-[#F5A623]/10' : 'text-gray-500 hover:text-gray-300 active:bg-white/5'}`}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                  <span className={`text-[10px] leading-tight ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>
                </button>
              );
            })}
            {overflow.length > 0 && (
              <div className="relative flex-1">
                <button onClick={() => setMoreOpen(!moreOpen)}
                  className={`w-full flex flex-col items-center justify-center gap-1 py-2.5 px-1 rounded-xl transition-all min-h-[60px] active:scale-95 ${moreOpen || overflow.some(i => isActivePath(pathname, i.path)) ? 'text-[#F5A623] bg-[#F5A623]/10' : 'text-gray-500 hover:text-gray-300 active:bg-white/5'}`}
                  data-testid="nav-more">
                  <MoreHorizontal className="w-5 h-5" />
                  <span className="text-[10px] font-medium">More</span>
                </button>
                {moreOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                    <div className="absolute bottom-full right-1 mb-2 w-52 bg-[#2A3240] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="nav-more-menu">
                      {overflow.map(({ path, icon: Icon, label }) => (
                        <button key={path} onClick={() => { navigate(path); setMoreOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm transition-colors ${isActivePath(pathname, path) ? 'text-[#F5A623] bg-[#F5A623]/10' : 'text-gray-300 hover:bg-white/5'}`}
                          data-testid={`nav-more-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                          <Icon className="w-5 h-5" />{label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
